import React from "react";
import { Link } from "react-router-dom";
import { Droplets, Flame, Truck, Wind, Waves, Layers, Gauge, ShieldCheck } from "lucide-react";
import SEO from "../../components/site/SEO";

const EQUIPMENT = [
  { icon: Gauge, t: "8 GPM · 3,000 PSI · fully adjustable", d: "Industrial-grade output, dialed to whatever the surface calls for — never the other way around." },
  { icon: Flame, t: "Cold, hot, and wet-steam operation", d: "Up to 248°F cuts through grease, gum, oil, and organic growth cold water simply can't touch." },
  { icon: Waves, t: "Low-pressure soft-wash system", d: "Dedicated chemical application for siding, roofs, and delicate surfaces — professional solutions do the work at a fraction of rental-machine pressure." },
  { icon: Layers, t: "Professional flat-surface cleaner", d: "Streak-free, spin-bar passes on concrete, pavers, and stamped surfaces — no zebra stripes." },
  { icon: Truck, t: "330-gallon onboard water tank", d: "Jobs don't depend on your spigot, well, or water pressure — we bring our own water." },
  { icon: Wind, t: "Belt-driven commercial pump", d: "Pump-protection system engineered for daily commercial use — not weekend rental duty." },
  { icon: ShieldCheck, t: "DOT-compliant commercial trailer rig", d: "Fully self-contained, so we arrive ready — 100-foot commercial steel-braided hose on professional reels." },
];

export default function About() {
  return (
    <>
      <SEO title="About — Wonder Washers Pressure Washing" description="Locally owned Oregon pressure washing crew backed by professional-contractor equipment. Learn about our commitment to residential-safe cleaning." canonical="[BOOKING_URL]/about" />
      <section className="bg-[color:var(--ww-navy)] text-white py-20 md:py-24 px-6 md:px-12">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-xs font-subhead tracking-widest text-[color:var(--ww-gold)] uppercase mb-3">About</div>
            <h1 className="font-display uppercase text-5xl md:text-6xl leading-none">Locally owned.<br /> Seriously reliable.</h1>
            <p className="mt-4 text-slate-300 leading-relaxed">
              Wonder Washers was built by Oregon locals who got tired of hiring pressure washing crews that ghosted, over-promised, and under-delivered. We show up, do the work, and leave your property looking new again — without stripped paint, blasted roofs, or fuzzed-up cedar.
            </p>
            <p className="mt-4 text-slate-300 leading-relaxed">
              We invested in the same professional-contractor rig commercial routes depend on — because commercial-grade equipment means faster jobs, better results, no reliance on the home's water supply, and the fine control to keep every surface safe. One rig, every job, done right.
            </p>
            <p className="mt-4 text-slate-300 leading-relaxed">
              Today we serve homes and businesses across Eugene, Springfield, and the Willamette Valley — with clean equipment, straight quotes, and Pacific Northwest pride.
            </p>
          </div>
          <div className="rounded-md overflow-hidden ww-card-lg">
            <img src="https://images.unsplash.com/photo-1740657254989-42fe9c3b8cce?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxjbGVhbmluZyUyMHNlcnZpY2UlMjBjcmV3fGVufDB8fHx8MTc4NTk1NjQ0Mnww&ixlib=rb-4.1.0&q=85" alt="Wonder Washers crew" className="w-full h-72 object-cover" loading="lazy" />
          </div>
        </div>
      </section>

      {/* OUR EQUIPMENT — replaces old 3-card "why us" grid (existing section repurposed) */}
      <section className="max-w-6xl mx-auto py-20 px-6 md:px-12">
        <div className="text-center mb-12">
          <div className="text-xs font-subhead tracking-widest text-[color:var(--ww-red)] uppercase mb-3">Our Equipment</div>
          <h2 className="font-display uppercase text-4xl md:text-5xl text-[color:var(--ww-navy)] leading-tight">Commercial-grade power. Residential-grade care.</h2>
          <p className="mt-4 text-slate-600 max-w-2xl mx-auto">
            The rig can deliver 3,000 PSI. The expertise is knowing when it shouldn't. Every job starts by matching pressure, temperature, and technique to the surface.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          {EQUIPMENT.map(({ icon: Icon, t, d }) => (
            <div key={t} className="ww-card p-6 flex gap-4">
              <span className="h-11 w-11 shrink-0 rounded-md bg-[color:var(--ww-navy)] text-[color:var(--ww-gold)] inline-flex items-center justify-center"><Icon className="h-5 w-5" /></span>
              <div>
                <div className="font-subhead uppercase tracking-wide text-[color:var(--ww-navy)]">{t}</div>
                <p className="mt-1 text-sm text-slate-600 leading-relaxed">{d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-4xl mx-auto py-8 pb-24 px-6 text-center">
        <Droplets className="h-8 w-8 text-[color:var(--ww-pressure-blue)] mx-auto" />
        <h2 className="mt-4 font-display uppercase text-4xl text-[color:var(--ww-navy)]">Come see what "clean" really looks like.</h2>
        <div className="mt-6 flex flex-wrap gap-3 justify-center">
          <Link to="/book" className="ww-btn-primary">Book Online</Link>
          <Link to="/contact" className="ww-btn-outline">Get in touch</Link>
        </div>
      </section>
    </>
  );
}
